import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Preloader } from '@smartshell/ui-kit';
import cn from 'classnames';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

import { AuthContext } from 'auth';
import {
  EHostModalType,
  HostPageModals,
  HostsAddButtons,
  QuickMenu,
  Sidebar,
  Statuses,
  useHostModals,
} from 'components';
import {
  useHostContextMenuItems,
  useSendShellCommand,
  useHostsData,
  useWindowResize,
  Host,
} from 'hooks';
import { LocalizationContext } from 'localization';
import { IUser } from 'types';

import s from './Computers.module.scss';
import { ComputersTable } from './ComputersTable';
import { shouldShowHostSidebar } from './responsive';
import { useHostsTablesState } from './useHostsTablesState';
import { getContextMenuFunctions } from './utilites';

const DEFAULT_GROUPS_FILTER = 'all';

export const Computers: FC = () => {
  const { t } = useContext(LocalizationContext);
  const windowSize = useWindowResize();
  const canShowHostSidebar = shouldShowHostSidebar(windowSize.width);

  const {
    data: { permissions },
  } = useContext(AuthContext);

  const location = useLocation();

  const [searchParams] = useSearchParams();
  const params = searchParams.get('groups')?.split(',') ?? [
    DEFAULT_GROUPS_FILTER,
  ];

  const [selectedClientUUID, setSelectedClientUUID] = useState<string | null>(
    null
  );

  const {
    groupsById,
    hosts,
    refetch,
    isFirstLoading,
    deleteHostGroup,
    stopPolling,
    statedPoling,
  } = useHostsData();

  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [defaultClient, setIsDefaultClient] = useState<string | undefined>('');
  const [isResetPasswordModal, setIsResetPasswordModal] = useState<
    boolean | undefined
  >(false);
  const [currentClient, setCurrentClient] = useState<IUser | undefined>(
    undefined
  );
  const [hostsByGroup, hasLockedHosts] = useMemo(() => {
    const hostsByGroupId: Record<number, Array<Host>> = {};
    let hasLockedHosts = false;

    // If it's manager - add empty groups
    if (permissions?.has('host_manage')) {
      Object.values(groupsById).forEach((group) => {
        hostsByGroupId[group.id] = [];
      });
    }

    hosts.forEach((host) => {
      if (host.locked) hasLockedHosts = true;

      if (hostsByGroupId[host.group_id]) {
        return hostsByGroupId[host.group_id].push(host);
      }

      hostsByGroupId[host.group_id] = [host];
    });

    return [hostsByGroupId, hasLockedHosts];
  }, [hosts, groupsById, permissions]);

  const hostModalsState = useHostModals(undefined, undefined, hosts);

  const { useControlledState } = useHostsTablesState(
    hasLockedHosts,
    hostModalsState.selectedHostsIds
  );

  const shouldRenderTable = (groupId: string): boolean => {
    return (
      !location.search ||
      params.includes(DEFAULT_GROUPS_FILTER) ||
      params.includes(groupId)
    );
  };

  const contextMenuItems = useMemo(
    () => useHostContextMenuItems(getContextMenuFunctions(hostModalsState)),
    [hostModalsState]
  );

  const addHost = useCallback(() => {
    hostModalsState.unselectHosts();
    hostModalsState.setType(EHostModalType.MANAGE_HOST);
  }, []);

  const addGroup = useCallback(() => {
    hostModalsState.setType(EHostModalType.MANAGE_HOST_GROUP);
  }, []);

  const navigate = useNavigate();

  const [
    selectedTabs,
    // setSelectedTabs
  ] = useState<Array<string>>(params.length ? params : [DEFAULT_GROUPS_FILTER]);

  // const tabs: Array<IButtonTabItem> = useMemo(
  //   () => [
  //     { title: t('groups.all'), value: DEFAULT_GROUPS_FILTER },
  //     ...Object.values(groupsById).map(({ title, id }) => ({
  //       title,
  //       value: id.toString(),
  //       unselectable: true,
  //     })),
  //   ],
  //   [groupsById, t]
  // );

  const { sendCommand, sendCommands } = useSendShellCommand();

  useEffect(() => {
    navigate(`?groups=${selectedTabs.join(',')}`, { replace: true });
  }, [selectedTabs]);

  useEffect(() => {
    if (!hostModalsState.selectedHostsIds) return;

    const keyboardListener = (event: KeyboardEvent): void => {
      if (event.code === 'KeyS' && event.altKey && event.ctrlKey) {
        const selectedHosts = hosts.filter((host) =>
          hostModalsState.selectedHostsIds.includes(host.id)
        );

        const onlineHosts = selectedHosts.filter((host) => host.online);
        const offlineHosts = selectedHosts.filter((host) => !host.online);

        if (onlineHosts.length === 1) {
          sendCommand({
            host_id: onlineHosts[0].id,
            action: 'SHUTDOWN',
          });
        } else if (onlineHosts.length > 1) {
          sendCommands({
            host_ids: onlineHosts.map((host) => host.id),
            action: 'SHUTDOWN',
          });
        }

        if (offlineHosts.length === 1) {
          sendCommand({
            host_id: offlineHosts[0].id,
            action: 'WOL',
          });
        } else if (offlineHosts.length > 1) {
          sendCommands({
            host_ids: offlineHosts.map((host) => host.id),
            action: 'WOL',
          });
        }
      }
    };

    document.addEventListener('keydown', keyboardListener);

    return () => document.removeEventListener('keydown', keyboardListener);
  }, [hostModalsState.selectedHostsIds, sendCommand, sendCommands, hosts]);

  useEffect(() => {
    if (hostModalsState.selectedHostsIds.length !== 1) {
      hostModalsState.setSidebarHostId(null);
    }
  }, [hostModalsState.selectedHostsIds.length]);

  useEffect(() => {
    if (!canShowHostSidebar) {
      hostModalsState.setSidebarHostId(null);
    }
  }, [canShowHostSidebar, hostModalsState.setSidebarHostId]);

  const [notPermissionModalOpen, setNotPermissionModalOpen] =
    useState<boolean>(false);

  const handleCloseDeposit = useCallback(() => {
    setIsDepositOpen(false);
    hostModalsState.setType(EHostModalType.NONE);
    setSelectedClientUUID(null);
  }, [hostModalsState]);

  const handleCloseResetPassword = useCallback(() => {
    setIsResetPasswordModal(false);
  }, []);

  const handleCloseClientModal = useCallback(() => {
    setSelectedClientUUID(null);
    hostModalsState.unselectHosts();
  }, [hostModalsState]);

  return (
    <Preloader loading={isFirstLoading || !permissions}>
      <div
        className={cn(
          s.container,
          !Object.values(groupsById).length && s.container_placeholder
        )}
      >
        <HostPageModals
          notPermissionModalOpen={notPermissionModalOpen}
          isDepositOpen={isDepositOpen}
          setIsDepositOpen={setIsDepositOpen}
          setIsDefaultClient={setIsDefaultClient}
          isResetPasswordModal={isResetPasswordModal}
          setIsResetPasswordModal={setIsResetPasswordModal}
          selectedClientUUID={selectedClientUUID}
          defaultClient={defaultClient}
          currentClient={currentClient}
          setCurrentClient={setCurrentClient}
          onCloseDeposit={handleCloseDeposit}
          onCloseResetPassword={handleCloseResetPassword}
          onCloseClientModal={handleCloseClientModal}
          onCloseNotPermission={() => setNotPermissionModalOpen(false)}
          hostModalsState={hostModalsState}
          refetch={refetch}
          stopPolling={stopPolling}
          statedPoling={statedPoling}
        />

        {canShowHostSidebar && hostModalsState.sidebarHostId && (
          <Sidebar
            onClose={() => {
              hostModalsState.setSidebarHostId(null);
              hostModalsState.unselectHosts();
            }}
            hostId={hostModalsState.sidebarHostId}
          />
        )}
        <div className={s.controls}>
          {/*<ButtonTabs*/}
          {/*  tabs={tabs}*/}
          {/*  selectedTabs={selectedTabs}*/}
          {/*  onTabSelect={(tab) => {*/}
          {/*    if (*/}
          {/*      selectedTabs.includes(DEFAULT_GROUPS_FILTER) ||*/}
          {/*      tab === DEFAULT_GROUPS_FILTER*/}
          {/*    ) {*/}
          {/*      return setSelectedTabs([tab]);*/}
          {/*    }*/}

          {/*    setSelectedTabs((state) => [...state, tab]);*/}
          {/*  }}*/}
          {/*  onTabUnselect={(tab) => {*/}
          {/*    if (selectedTabs.length === 1) {*/}
          {/*      return setSelectedTabs([DEFAULT_GROUPS_FILTER]);*/}
          {/*    }*/}

          {/*    setSelectedTabs((state) => state.filter((item) => item !== tab));*/}
          {/*  }}*/}
          {/*/>*/}
          <Statuses />

          {permissions?.has('host_manage') && (
            <HostsAddButtons
              hostsCount={hosts.length}
              addHost={addHost}
              addGroup={addGroup}
              isGroups={Boolean(Object.values(groupsById).length)}
            />
          )}
        </div>
        {!Object.values(groupsById).length && (
          <div className={s.placeholder}>
            {t('computers.groups.placeholder')}
          </div>
        )}
        {Object.entries(hostsByGroup).map(
          ([groupId, hosts]) =>
            shouldRenderTable(groupId) && (
              <ComputersTable
                key={`computers-${groupId}`}
                group={groupsById[Number(groupId)]}
                hosts={hosts}
                openClientModal={(clientUUID) => {
                  if (!permissions?.has('client_info')) {
                    setNotPermissionModalOpen(true);
                    return;
                  }

                  setSelectedClientUUID(clientUUID);
                }}
                openShopModal={hostModalsState.openShop}
                openHostGroupEditor={(id) => {
                  hostModalsState.setType(EHostModalType.MANAGE_HOST_GROUP);
                  hostModalsState.setGroupId(id);
                }}
                deleteHostGroup={deleteHostGroup}
                useControlledState={useControlledState}
                onRowClick={(event, hostId, rowIndex, flatRows) => {
                  if (!canShowHostSidebar) {
                    hostModalsState.setSelectedHostsIds([hostId]);
                    hostModalsState.setSidebarHostId(null);
                    return;
                  }

                  hostModalsState.onHostClick(
                    event,
                    hostId,
                    rowIndex,
                    flatRows
                  );
                }}
                contextMenuItems={contextMenuItems}
                setSelectedHostsIds={hostModalsState.setSelectedHostsIds}
                selectedHostsIds={hostModalsState.selectedHostsIds}
              />
            )
        )}
        {hostModalsState.selectedHostsIds.length > 1 && (
          <QuickMenu
            hosts={hosts}
            onClose={() => hostModalsState.setSelectedHostsIds([])}
            selectedItems={
              new Set(hostModalsState.selectedHostsIds.map(String))
            }
            openStartSeanceModal={() =>
              hostModalsState.setType(EHostModalType.SHOP)
            }
            openBookingModal={() =>
              hostModalsState.setType(EHostModalType.BOOKING)
            }
            openSendMessageModal={() =>
              hostModalsState.setType(EHostModalType.SEND_MESSAGE)
            }
            mod={true}
          />
        )}
      </div>
    </Preloader>
  );
};
